import { type ThemeOptions } from '@mui/material/styles';

export const darkThemeOptions = {
  components: {
    MuiAlert: {
      // v9 removed combined variant+color slots (filledError, etc.); express them via `variants`. Every Alert in the app uses `severity` (not `color`).
      variants: [
        { props: { variant: 'filled', severity: 'error' }, style: { backgroundColor: '#F44336', color: '#FCFDFF' } },
        { props: { variant: 'filled', severity: 'info' }, style: { backgroundColor: '#29B6F6', color: '#0B0F19' } },
        { props: { variant: 'filled', severity: 'success' }, style: { backgroundColor: '#26A69A', color: '#0B0F19' } },
        { props: { variant: 'filled', severity: 'warning' }, style: { backgroundColor: '#FFA726', color: '#0B0F19' } },
        { props: { variant: 'outlined', severity: 'error' }, style: { borderColor: '#F44336', color: '#F4C7C7' } },
        { props: { variant: 'outlined', severity: 'info' }, style: { borderColor: '#29B6F6', color: '#B8E7FB' } },
        { props: { variant: 'outlined', severity: 'success' }, style: { borderColor: '#26A69A', color: '#CCE8E4' } },
        { props: { variant: 'outlined', severity: 'warning' }, style: { borderColor: '#FFA726', color: '#FFE2B7' } },
        { props: { variant: 'standard', severity: 'error' }, style: { backgroundColor: '#291414', color: '#F4C7C7' } },
        { props: { variant: 'standard', severity: 'info' }, style: { backgroundColor: '#0C2029', color: '#B8E7FB' } },
        {
          props: { variant: 'standard', severity: 'success' },
          style: { backgroundColor: '#1A2928', color: '#CCE8E4' },
        },
        {
          props: { variant: 'standard', severity: 'warning' },
          style: { backgroundColor: '#291D0B', color: '#FFE2B7' },
        },
      ],
    },
  },
  palette: {
    action: {
      active: 'rgba(252, 253, 255, 0.56)',
      disabled: 'rgba(252, 253, 255, 0.38)',
      disabledBackground: 'rgba(252, 253, 255, 0.12)',
      focus: 'rgba(252, 253, 255, 0.12)',
      hover: 'rgba(252, 253, 255, 0.08)',
      selected: 'rgba(252, 253, 255, 0.16)',
    },
    background: {
      default: '#0B0F19',
      'paper-elevation-0': '#0B0F19',
      'paper-elevation-1': '#111824',
      'paper-elevation-2': '#141A2A',
      'paper-elevation-3': '#171D2C',
      'paper-elevation-4': '#191F2E',
      'paper-elevation-5': '#1C2130',
      'paper-elevation-6': '#1E2433',
      'paper-elevation-7': '#1E2433',
      'paper-elevation-8': '#212635',
      'paper-elevation-9': '#212635',
      'paper-elevation-10': '#232937',
      'paper-elevation-11': '#232937',
      'paper-elevation-12': '#262B3A',
      'paper-elevation-13': '#262B3A',
      'paper-elevation-14': '#262B3A',
      'paper-elevation-15': '#282E3C',
      'paper-elevation-16': '#282E3C',
      'paper-elevation-17': '#282E3C',
      'paper-elevation-18': '#282E3C',
      'paper-elevation-19': '#282E3C',
      'paper-elevation-20': '#2B303E',
      'paper-elevation-21': '#2B303E',
      'paper-elevation-22': '#2B303E',
      'paper-elevation-23': '#2B303E',
      'paper-elevation-24': '#2B303E',
    },
    common: {
      black_states: {
        hover: 'rgba(11, 15, 25, 0.08)',
        selected: 'rgba(11, 15, 25, 0.16)',
        focus: 'rgba(11, 15, 25, 0.12)',
        focusVisible: 'rgba(11, 15, 25, 0.3)',
        outlinedBorder: 'rgba(11, 15, 25, 0.5)',
      },
      white_states: {
        focus: 'rgba(252, 253, 255, 0.12)',
        focusVisible: 'rgba(252, 253, 255, 0.3)',
        hover: 'rgba(252, 253, 255, 0.08)',
        main: '#FFFFFF',
        outlinedBorder: 'rgba(252, 253, 255, 0.5)',
        selected: 'rgba(252, 253, 255, 0.16)',
      },
    },
    error: {
      contrastText: '#FCFDFF',
      main: '#F44336',
      _states: {
        hover: 'rgba(244, 67, 54, 0.08)',
        selected: 'rgba(244, 67, 54, 0.16)',
        focus: 'rgba(244, 67, 54, 0.12)',
        focusVisible: 'rgba(244, 67, 54, 0.3)',
        outlinedBorder: 'rgba(244, 67, 54, 0.5)',
      },
    },
    info: {
      contrast: '#111824',
      contrastText: '#0B0F19',
      main: '#29B6F6',
      _states: {
        hover: 'rgba(41, 182, 246, 0.08)',
        selected: 'rgba(41, 182, 246, 0.16)',
        focus: 'rgba(41, 182, 246, 0.12)',
        focusVisible: 'rgba(41, 182, 246, 0.3)',
        outlinedBorder: 'rgba(41, 182, 246, 0.5)',
      },
    },
    primary: {
      _states: {
        hover: 'rgba(148, 148, 255, 0.08)',
        selected: 'rgba(148, 148, 255, 0.16)',
        focus: 'rgba(148, 148, 255, 0.12)',
        focusVisible: 'rgba(148, 148, 255, 0.3)',
        outlinedBorder: 'rgba(148, 148, 255, 0.5)',
      },
      contrast: '#111824',
      contrastText: '#0B0F19',
      dark: '#6047FF',
      light: '#C2C5FF',
      main: '#9494FF',
    },
    secondary: {
      _states: {
        hover: 'rgba(100, 181, 246, 0.08)',
        selected: 'rgba(100, 181, 246, 0.16)',
        focus: 'rgba(100, 181, 246, 0.12)',
        focusVisible: 'rgba(100, 181, 246, 0.3)',
        outlinedBorder: 'rgba(100, 181, 246, 0.5)',
      },
      contrast: '#FFFFFF',
      contrastText: '#0B0F19',
      dark: '#1976D2',
      light: '#BBDEFB',
      main: '#64B5F6',
    },
    // Serverless spaces, told apart from the primary-coloured dedicated clusters.
    serverless: {
      main: '#CE93D8',
      _states: {
        selected: 'rgba(206, 147, 216, 0.16)',
      },
    },
    success: {
      _states: {
        hover: 'rgba(38, 166, 154, 0.08)',
        selected: 'rgba(38, 166, 154, 0.16)',
        focus: 'rgba(38, 166, 154, 0.12)',
        focusVisible: 'rgba(38, 166, 154, 0.3)',
        outlinedBorder: 'rgba(38, 166, 154, 0.5)',
      },
      contrast: '#111824',
      contrastText: '#0B0F19',
      dark: '#00796B',
      light: '#4DB6AC',
      main: '#26A69A',
    },
    text: {
      primary: '#FCFDFF',
      secondary: '#ABB1C7',
      disabled: '#4E5366',
      _states: {
        hover: 'rgba(252, 253, 255, 0.08)',
        selected: 'rgba(252, 253, 255, 0.16)',
        focus: 'rgba(252, 253, 255, 0.12)',
        focusVisible: 'rgba(252, 253, 255, 0.3)',
      },
    },
    warning: {
      contrastText: '#0B0F19',
      contrast: '#111824',
      main: '#FFA726',
      dark: '#F57C00',
      _states: {
        hover: 'rgba(255, 167, 38, 0.08)',
        selected: 'rgba(255, 167, 38, 0.16)',
        focus: 'rgba(255, 167, 38, 0.12)',
        focusVisible: 'rgba(255, 167, 38, 0.3)',
        outlinedBorder: 'rgba(255, 167, 38, 0.5)',
      },
    },
  },
} satisfies ThemeOptions;
